import { logger } from "../utils/logger";

const SW_URL = "/sw.js";

// Un tag fijo hace que los avisos se reemplacen entre sí en vez de apilarse:
// con 5 correos seguidos el usuario ve un aviso actualizado, no 5 notificaciones.
const MAIL_NOTIFICATION_TAG = "mail-unread";

export type MailNotificationPermission = NotificationPermission | "unsupported";

let registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;

export function mailNotificationsSupported(): boolean {
  return (
    typeof window !== "undefined"
    && "Notification" in window
    && "serviceWorker" in navigator
    // Notification exige contexto seguro (https). En http quedaría siempre denegado.
    && window.isSecureContext
  );
}

export function mailNotificationPermission(): MailNotificationPermission {
  if (!mailNotificationsSupported()) return "unsupported";
  return Notification.permission;
}

export function registerMailNotificationWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!mailNotificationsSupported()) return Promise.resolve(null);
  if (registrationPromise) return registrationPromise;

  registrationPromise = navigator.serviceWorker
    .register(SW_URL)
    .catch((error: unknown) => {
      logger.error("No se pudo registrar el service worker de notificaciones:", error);
      registrationPromise = null;
      return null;
    });

  return registrationPromise;
}

/**
 * Pide permiso al usuario. Debe llamarse desde un gesto explícito: el navegador
 * permite pedirlo una sola vez y, si lo bloquean, la app ya no puede volver a
 * preguntar (hay que desbloquearlo a mano en la configuración del navegador).
 */
export async function requestMailNotificationPermission(): Promise<MailNotificationPermission> {
  if (!mailNotificationsSupported()) return "unsupported";

  try {
    const result = await Notification.requestPermission();
    if (result === "granted") {
      await registerMailNotificationWorker();
    }
    return result;
  } catch (error: unknown) {
    logger.error("Error pidiendo permiso de notificaciones:", error);
    return Notification.permission;
  }
}

/**
 * Muestra el aviso de correo nuevo. Devuelve true si se pudo mostrar.
 *
 * El texto es genérico a propósito: no incluye remitente ni asunto. Una
 * notificación del sistema aparece en pantallas compartidas y en la pantalla
 * bloqueada, así que mostrar datos del huésped ahí sería una fuga de PII.
 * Además el evento WebSocket solo trae contadores, no trae esos datos.
 */
export async function showMailNotification(unreadCount: number): Promise<boolean> {
  if (!mailNotificationsSupported() || Notification.permission !== "granted") {
    return false;
  }

  const registration = await registerMailNotificationWorker();
  if (!registration) return false;

  const body = unreadCount === 1
    ? "Tenés 1 correo sin leer."
    : `Tenés ${unreadCount} correos sin leer.`;

  try {
    await registration.showNotification("Correo nuevo", {
      body,
      tag: MAIL_NOTIFICATION_TAG,
      icon: "/favicon.ico",
      badge: "/favicon.ico",
      data: { url: "/correo" },
    });
    return true;
  } catch (error: unknown) {
    logger.error("No se pudo mostrar la notificación de correo:", error);
    return false;
  }
}

/** Aviso de confirmación al activar, para que se vea que quedó funcionando. */
export async function showMailNotificationEnabled(): Promise<boolean> {
  if (!mailNotificationsSupported() || Notification.permission !== "granted") {
    return false;
  }

  const registration = await registerMailNotificationWorker();
  if (!registration) return false;

  try {
    await registration.showNotification("Avisos de correo activados", {
      body: "Te vamos a avisar acá cuando llegue un correo nuevo.",
      tag: MAIL_NOTIFICATION_TAG,
      icon: "/favicon.ico",
      badge: "/favicon.ico",
      data: { url: "/correo" },
    });
    return true;
  } catch (error: unknown) {
    logger.error("No se pudo mostrar la notificación de confirmación:", error);
    return false;
  }
}
